import { z } from "zod";

export interface HarnessSpecShape {
	id: string;
	label: string;
	description: string;
	providers: readonly string[] | null;
	toolsSelectable: boolean;
	/**
	 * Whether this harness drives a model. A harness that serves HTTP instead
	 * of running turns has no model, no system prompt, and no thinking level:
	 * every surface that validates or edits those skips them when this is off.
	 */
	requiresModel: boolean;
	/** Whether a chat session (console chat, /api/chat, triggers) can target it. */
	supportsChat: boolean;
}

/**
 * The single primitive for every harness-aware surface in Forgedock.
 * Additions belong here; IDs, schemas, types, and lookup data are derived below.
 */
export const HARNESS_SPECS = [
	{
		id: "pi",
		label: "Pi Agent",
		description: "Forgedock's native harness: any provider, selectable tools.",
		providers: null,
		toolsSelectable: true,
		requiresModel: true,
		supportsChat: true,
	},
	{
		id: "worker",
		label: "Worker",
		description:
			"Code that runs beside your agents: a fetch handler served at /w/<slug>, behind the same sealed egress. No model.",
		providers: [],
		toolsSelectable: false,
		requiresModel: false,
		supportsChat: false,
	},
] as const satisfies readonly HarnessSpecShape[];

export type Harness = (typeof HARNESS_SPECS)[number]["id"];
export type HarnessSpec = Omit<HarnessSpecShape, "id"> & { id: Harness };

export const HARNESSES = HARNESS_SPECS.map((spec) => spec.id) as [Harness, ...Harness[]];

export const harnessSchema = z.enum(HARNESSES).default("pi");

export const HARNESS_CATALOG = Object.fromEntries(
	HARNESS_SPECS.map((spec) => [spec.id, spec]),
) as Record<Harness, HarnessSpec>;

/** Whether the harness runs a model at all (see HarnessSpecShape.requiresModel). */
export function harnessRequiresModel(harness: Harness): boolean {
	return HARNESS_CATALOG[harness].requiresModel;
}

/** Whether a chat session can be opened against this harness. */
export function harnessSupportsChat(harness: Harness): boolean {
	return HARNESS_CATALOG[harness].supportsChat;
}

function modelProvider(modelRef: string): string | null {
	const separator = modelRef.indexOf(":");
	if (separator <= 0 || separator === modelRef.length - 1) return null;
	return modelRef.slice(0, separator).trim().toLowerCase() || null;
}

export function isModelAllowedForHarness(harness: Harness, modelRef: string): boolean {
	const spec = HARNESS_CATALOG[harness];
	// A model-less harness has no opinion: whatever a row carries is irrelevant
	// to it, so it is never a mismatch.
	if (!spec.requiresModel) return true;
	const allowedProviders = spec.providers;
	if (allowedProviders === null) return true;
	const provider = modelProvider(modelRef);
	return provider !== null && allowedProviders.includes(provider);
}

export function harnessModelError(harness: Harness, modelRef: string): string | null {
	if (isModelAllowedForHarness(harness, modelRef)) return null;
	const spec = HARNESS_CATALOG[harness];
	const providerDescription = spec.providers?.join(", ") ?? "any";
	return `Model '${modelRef}' is not supported by ${spec.label}; allowed providers: ${providerDescription}`;
}
